import { Injectable } from '@angular/core';
import { LoggingService, Logger } from './logging.service';

/**
 * Logs snapshots of FullStory's client-side state to the logging pipeline
 * (Datadog) so sessions where FullStory fails to record can be diagnosed
 * remotely — e.g. integrated/embedded flows running inside an iframe, where
 * fs.js silently refuses to record unless window['_fs_run_in_iframe'] is set.
 */
@Injectable({ providedIn: 'root' })
export class FullStoryDiagnosticsService {
  private logger: Logger;
  private snapshotsScheduled = false;

  constructor(private _loggingService: LoggingService) {
    this.logger = this._loggingService.rootLogger.newLogger('FullStoryDiagnosticsService');
  }

  /**
   * Schedule diagnostic snapshots shortly after Segment is loaded.
   *
   * Two snapshots are taken: one early (FullStory should be initializing)
   * and one late (FullStory should be fully loaded and recording). No-op if
   * snapshots were already scheduled for this app session.
   */
  public scheduleSnapshots(): void {
    if (this.snapshotsScheduled) {
      return;
    }
    this.snapshotsScheduled = true;
    setTimeout(() => this.captureSnapshot('5s after Segment load'), 5000);
    setTimeout(() => this.captureSnapshot('30s after Segment load'), 30000);
  }

  /**
   * Log one snapshot of FullStory's observable client-side state.
   *
   * Args:
   *     label: Human-readable marker of when the snapshot was taken.
   */
  public captureSnapshot(label: string): void {
    const fs = (window as any).FS;
    // The Segment snippet stub defines identify/event/etc.; only the real
    // fs.js recorder adds getCurrentSessionURL. A stub without it means the
    // recorder script never loaded (blocked, errored, or refused).
    const recorderLoaded = !!fs && typeof fs.getCurrentSessionURL === 'function';

    let sessionUrl: string | null = null;
    if (recorderLoaded) {
      try {
        sessionUrl = fs.getCurrentSessionURL(true) || null;
      } catch (err) {
        this.logger.error('Unable to read FullStory session URL', { errorObject: err });
      }
    }

    let inIframe: boolean | 'unknown' = 'unknown';
    try {
      inIframe = window.self !== window.top;
    } catch (err) {
      // Cross-origin frame access can throw; that itself means we are framed.
      inIframe = true;
    }

    this.logger.info('FullStory diagnostic snapshot', {
      snapshotLabel: label,
      fsGlobalPresent: !!fs,
      fsRecorderLoaded: recorderLoaded,
      fsSessionUrl: sessionUrl,
      fsRecording: !!sessionUrl,
      fsReadyFired: this._loggingService.rootLogger.libraries?.fullstory != null,
      fsScriptTagPresent: !!document.querySelector('script[src*="fullstory.com"]'),
      inIframe: inIframe,
      fsRunInIframe: (window as any)._fs_run_in_iframe ?? null,
      fsOrg: (window as any)._fs_org ?? null,
      fsDebug: (window as any)._fs_debug ?? null,
      fsCookieDomain: (window as any)._fs_cookie_domain ?? null,
      fsIsOuterScript: (window as any)._fs_is_outer_script ?? null,
    });
  }
}
